<p class="text-2xl my-8">Our Track Record</p>
<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
  @for (item of trackRecord; track item.label) {
    <div class="bg-white rounded p-4 flex flex-col gap-2">
      <div class="flex items-center gap-2 text-Gray-800">
        <div class="bg-accent-blue_light rounded flex items-center justify-center w-6 h-6">
          <span class="material-symbols-rounded text-accent-blue_dark text-xl">{{ item.icon }}</span>
        </div>
        <span class="text-base">{{ item.label }}</span>
      </div>
      <p class="text-2xl font-bold">{{ item.value }}</p>
    </div>
  }
</div>

<!-- Desktop filters -->
<div #filterBar class="transition-all duration-300 ease-in-out"
  [ngClass]="{'fixed top-0 left-0 right-0 bg-white z-50 shadow': isScrolled()}">
  <div [ngClass]="{'max-w-[1400px] mx-auto px-4 py-2': isScrolled()}">
    <div class="mb-6 hidden lg:flex gap-4 flex-wrap" [ngClass]="{'mb-0': isScrolled()}">
      <p-multiselect
        [(ngModel)]="selectedPropertyTypes"
        [options]="filterOptions().propertyTypes"
        placeholder="Property Type"
        display="chip"
        class="w-48"
        [showClear]="true"
        (onChange)="onFilterChange()" />
      <p-multiselect
        [(ngModel)]="selectedLoanPurposes"
        [options]="filterOptions().loanPurposes"
        placeholder="Loan Purpose"
        display="chip"
        class="w-48"
        [showClear]="true"
        (onChange)="onFilterChange()" />
      <p-multiselect
        [(ngModel)]="selectedStates"
        [options]="stateOptions()"
        optionLabel="label"
        optionValue="value"
        placeholder="Property State"
        display="chip"
        class="w-48"
        [filter]="true"
        filterPlaceholder="Search"
        [showClear]="true"
        emptyFilterMessage="No property was funded here"
        (onChange)="onFilterChange()" />
      <p-select
        [(ngModel)]="selectedLoanAmount"
        [options]="loanAmountOptions"
        optionLabel="label"
        placeholder="Loan Amount"
        class="w-48"
        [showClear]="true"
        (onChange)="onFilterChange()" />
    </div>
  </div>
</div>

<!-- Mobile filter button -->
<div class="lg:hidden mb-4">
  <button class="secondary-btn flex items-center gap-2" (click)="mobileFiltersVisible = true">
    <span class="material-symbols-rounded text-base">tune</span>
    Filter
    @if (activeFilterCount > 0) {
      <span class="bg-accent-blue_dark text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{{ activeFilterCount }}</span>
    }
  </button>
</div>

<!-- Mobile filters drawer -->
<p-drawer [(visible)]="mobileFiltersVisible" header="Filters" position="bottom" styleClass="h-auto">
  <div class="flex flex-col gap-4 pb-4">
    <div class="flex flex-col gap-1">
      <label class="text-sm text-Gray-800">Property Type</label>
      <p-multiselect
        [(ngModel)]="selectedPropertyTypes"
        [options]="filterOptions().propertyTypes"
        placeholder="Property Type"
        display="chip"
        class="w-full"
        [showClear]="true"
        (onChange)="onFilterChange()" />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm text-Gray-800">Loan Purpose</label>
      <p-multiselect
        [(ngModel)]="selectedLoanPurposes"
        [options]="filterOptions().loanPurposes"
        placeholder="Loan Purpose"
        display="chip"
        class="w-full"
        [showClear]="true"
        (onChange)="onFilterChange()" />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm text-Gray-800">Property State</label>
      <p-multiselect
        [(ngModel)]="selectedStates"
        [options]="stateOptions()"
        optionLabel="label"
        optionValue="value"
        placeholder="Property State"
        display="chip"
        class="w-full"
        [filter]="true"
        filterPlaceholder="Search"
        [showClear]="true"
        emptyFilterMessage="No property was funded here"
        (onChange)="onFilterChange()" />
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm text-Gray-800">Loan Amount</label>
      <p-select
        [(ngModel)]="selectedLoanAmount"
        [options]="loanAmountOptions"
        optionLabel="label"
        placeholder="Loan Amount"
        class="w-full"
        [showClear]="true"
        (onChange)="onFilterChange()" />
    </div>
    <div class="flex gap-3 mt-2">
      <button class="secondary-btn flex-1 flex items-center justify-center gap-2 text-accent-red border-accent-red" (click)="clearFilters()">
        <span class="material-symbols-rounded text-base">close</span>
        Clear all
      </button>
      <button class="main-btn flex-1" (click)="mobileFiltersVisible = false">Show results</button>
    </div>
  </div>
</p-drawer>

<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
  @for (deal of deals(); track deal.id) {
    <div class="bg-white rounded overflow-hidden flex flex-col">
      <div class="relative w-full h-[196px]">
        <app-image [id]="deal.id || ''" [name]="deal.defaultPhoto || ''" [alt]="deal.propertyName || ''" />
      </div>
      <div class="p-4 flex flex-col gap-3 flex-1">
        <div class="pb-3 border-b">
          <p class="font-semibold text-base leading-snug">{{ deal.propertyName }}</p>
          <p class="text-Gray-800 text-sm mt-0.5">{{ deal.propertyCity }}, {{ deal.propertyState }}</p>
        </div>
        <div class="flex flex-col gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-Gray-800">Property Type</span>
            <span class="font-medium text-right">{{ deal.propertyType }}{{ deal.propertySubType ? ', ' + deal.propertySubType : '' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-Gray-800">Loan Purpose</span>
            <span class="font-medium">{{ deal.loanPurpose }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-Gray-800">Loan Amount</span>
            <span class="font-medium">{{ deal.loanAmount | currency : 'USD' : 'symbol' : '1.2-2' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-Gray-800">DSCR</span>
            <span class="font-medium">{{ deal.dscr | number : '1.2-2' }}x</span>
          </div>
          <div class="flex justify-between">
            <span class="text-Gray-800">LTV</span>
            <span class="font-medium">{{ deal.ltv | number : '1.2-2' }}%</span>
          </div>
          <div class="flex justify-between">
            <span class="text-Gray-800">Yield Delivered</span>
            <span class="font-semibold text-green-600">{{ deal.participantYield | number : '1.2-2' }}%</span>
          </div>
        </div>
      </div>
    </div>
  }
</div>

@if (deals().length > 0) {
  <div class="flex items-center justify-between mt-6">
    <p class="text-Gray-800 text-sm">Showing {{ deals().length }} of {{ totalElements() }} deals</p>
    @if (deals().length < totalElements()) {
      <button class="secondary-btn" (click)="loadMore()" [disabled]="loading()">Load more</button>
    }
  </div>
}
